import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { ClientLineDescription } from "../../../../../client/components/ClientLineDescription";
import { LoanContract } from "../../../../types";
import { ContractAgentsData } from "../../../ContractAgentsData";
import { ContractClientsData } from "../../../ContractClientsData";
import { createCancellationDateDescriptionItem } from "../../../description-items/cancellationDateDescriptionItem";
import { createNoteDescriptionItem } from "../../../description-items/noteDescriptionItem";

interface Props {
  contract: LoanContract;
}

export const LoanContractDataSection = ({ contract }: Props) => {
  const { t } = useTranslation();

  const client = contract.clients[contract.policyHolderIndex];
  const coDebtor = isDefined(contract.coDebtorIndex) ? contract.clients[contract.coDebtorIndex] : undefined;

  return (
    <Descriptions
      bordered
      column={1}
      styles={{ label: { width: 180, fontWeight: "bold" } }}
      items={[
        {
          label: t("contract.sections.clients"),
          children: client && (
            <ContractClientsData
              items={[
                {
                  label: t("client.helpers.loanPolicyHolder"),
                  children: <ClientLineDescription client={client} />,
                },
                coDebtor
                  ? {
                      label: t("client.enums.formType.CO_DEBTOR"),
                      children: <ClientLineDescription client={coDebtor} />,
                    }
                  : undefined,
              ].filter((item) => !!item)}
            />
          ),
        },
        {
          label: t("contract.sections.contractData"),
          children: (
            <Descriptions
              bordered
              size="small"
              column={3}
              items={[
                {
                  label: t("contract.attrs.mediationReportSignDate"),
                  children: contract.mediationReportSignDate
                    ? formatLocaleDate(contract.mediationReportSignDate)
                    : undefined,
                },
                {
                  label: t("contract.attrs.applicationSignDate"),
                  children: formatLocaleDate(contract.applicationSignDate),
                },
                {
                  label: t("contract.attrs.signDate"),
                  children: formatLocaleDate(contract.signDate),
                },
                {
                  label: t("contract.attrs.loanMaturityDate"),
                  children: formatLocaleDate(contract.loanMaturityDate),
                },
                createCancellationDateDescriptionItem(contract),
                {
                  label: t("contract.attrs.ltvRatio"),
                  children: isDefined(contract.ltvRatio) ? formatLocalePercentageNumber(contract.ltvRatio) : undefined,
                },
                {
                  label: t("contract.attrs.approvedAmount"),
                  children: formatLocaleCurrency(contract.approvedAmount),
                },
                {
                  label: t("contract.attrs.monthlyPaymentAmount"),
                  children: formatLocaleCurrency(contract.monthlyPaymentAmount),
                },
                {
                  label: t("contract.attrs.interestRate"),
                  children: formatLocalePercentageNumber(contract.interestRate),
                },
                {
                  label: t("contract.enums.loanRateType._label"),
                  children: t(`contract.enums.loanRateType.${contract.rateType}`),
                },
                {
                  label: t("contract.attrs.fixationAnniversaryDate"),
                  children: formatLocaleDate(contract.fixationAnniversaryDate),
                },
                {
                  label: t("contract.attrs.contactClientDate"),
                  children: formatLocaleDate(contract.contactClientDate),
                },
                createNoteDescriptionItem(contract),
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.agents"),
          children: <ContractAgentsData contract={contract} />,
        },
      ]}
    />
  );
};
